import type { AppState } from '../../state/appState.js';
import { findFirstLayerNodeIds } from '../../scene/sceneQueries.js';
import type {
  Scene,
  SceneNode,
  SceneNodeId,
} from '../../scene/sceneState.js';
import type { LayerDef } from '../../scene/layerState.js';
import { clamp } from '../../math/scalar.js';
import {
  cloneSceneNode,
} from './clone.js';

export interface SceneTarget {
  scene: Scene;
  componentId: string | null;
}

export function getSceneTargetFor(appState: AppState, componentId: string | null): SceneTarget {
  if (componentId) {
    const component = appState.componentLibrary[componentId];
    if (component) return { scene: component.scene, componentId };
  }
  return { scene: appState.scene, componentId: null };
}

export function findSceneTargetByNodeIdFor(appState: AppState, nodeId: SceneNodeId): SceneTarget | null {
  if (appState.scene.nodes[nodeId]) return { scene: appState.scene, componentId: null };
  for (const component of Object.values(appState.componentLibrary)) {
    if (component.scene.nodes[nodeId]) return { scene: component.scene, componentId: component.id };
  }
  return null;
}

export interface DetachedSceneNode {
  target: SceneTarget;
  parentId: SceneNodeId | null;
  node: SceneNode;
}

export function detachSceneNodeFor(appState: AppState, nodeId: SceneNodeId): DetachedSceneNode | null {
  const target = findSceneTargetByNodeIdFor(appState, nodeId);
  const node = target?.scene.nodes[nodeId];
  if (!target || !node) return null;

  const parentId = findSceneParentId(target.scene, nodeId);
  removeChildId(target.scene, parentId, nodeId);
  delete target.scene.nodes[nodeId];

  return { target, parentId, node };
}

export function collectSceneSubtree(scene: Scene, rootId: SceneNodeId): Scene['nodes'] {
  const nodes: Scene['nodes'] = {};

  function visit(nodeId: SceneNodeId): void {
    const node = scene.nodes[nodeId];
    if (!node) return;
    nodes[nodeId] = cloneSceneNode(node);
    if (node.kind === 'frame') {
      for (const childId of node.childIds) visit(childId);
    }
  }

  visit(rootId);
  return nodes;
}

export function insertSceneNode<T extends SceneNode>(
  scene: Scene,
  parentId: SceneNodeId | null,
  node: T,
  index: number,
): T {
  const nextNode = cloneSceneNode(node) as T;
  scene.nodes[node.id] = nextNode;
  insertChildId(scene, parentId, node.id, index);
  return nextNode;
}

export function insertChildId(scene: Scene, parentId: SceneNodeId | null, childId: SceneNodeId, index: number): void {
  if (parentId) {
    const parent = scene.nodes[parentId];
    if (parent && parent.kind === 'frame') {
      const nextIndex = clamp(index, 0, parent.childIds.length);
      parent.childIds.splice(nextIndex, 0, childId);
      return;
    }
  }
  const nextIndex = clamp(index, 0, scene.rootIds.length);
  scene.rootIds.splice(nextIndex, 0, childId);
}

export function removeChildId(scene: Scene, parentId: SceneNodeId | null, childId: SceneNodeId): void {
  if (parentId) {
    const parent = scene.nodes[parentId];
    if (parent && parent.kind === 'frame') {
      parent.childIds = parent.childIds.filter(id => id !== childId);
      return;
    }
  }
  scene.rootIds = scene.rootIds.filter(id => id !== childId);
}

export function findSceneParentId(scene: Scene, nodeId: SceneNodeId): SceneNodeId | null {
  for (const [id, node] of Object.entries(scene.nodes)) {
    if (node.kind === 'frame' && node.childIds.includes(nodeId)) return id;
  }
  return null;
}

export function getLayerDefFor(
  appState: AppState,
  componentId: string | null | undefined,
  preferredLayerId?: string,
): { layerDef: LayerDef; layerId: string } | null {
  const scene = componentId ? appState.componentLibrary[componentId]?.scene : appState.scene;
  if (!scene) return null;
  if (preferredLayerId) {
    const layerNode = Object.values(scene.nodes).find(node => node.kind === 'layer' && node.layerId === preferredLayerId);
    if (layerNode?.kind === 'layer') {
      const layerDef = appState.layerLibrary[preferredLayerId];
      if (layerDef) return { layerDef, layerId: preferredLayerId };
    }
  }
  const ids = findFirstLayerNodeIds(scene);
  if (!ids) return null;
  const layerDef = appState.layerLibrary[ids.layerId];
  if (!layerDef) return null;
  return { layerDef, layerId: ids.layerId };
}
